import { fg } from '@atlassian/jira-feature-gating';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { toErrorId, toErrorMessage } from '@atlassian/jira-project-settings-apps-common/src/types';
import { famaCatchAndLogFetchError } from '@atlassian/jira-project-settings-apps-common/src/util/catch-and-log-fetch-error';
import { logError } from '@atlassian/jira-project-settings-apps-common/src/util/error-logger';
import { toAccountId } from '@atlassian/jira-shared-types/src/general.tsx';
import { PACKAGE_NAME } from '../../model/constants';
import type { AssigneeType, AvatarDetails, ProjectCategory, ProjectLead } from '../../model/types';
import type { ProjectContext, ProjectDetails } from './types';

/**
 * Response payload type for requests to /rest/api/2/project/{projectIdOrKey}. Note that fields
 * that are not consumed are not included in the type.
 */
export type ServerProjectDetailsResponse = {
	id: string;
	key: string;
	name: string;
	url: string;
	description: string;
	avatarUrls: {
		[key: string]: string;
	};
	simplified: boolean;
	projectCategory: ProjectCategory | undefined;
	assigneeType: AssigneeType;
	lead: ProjectLead;
	projectDescription: string;
	projectTypeKey: string;
	projectKeys: string[];
};

export type ServerProjectTeamTypeResponse = {
	teamType: string | undefined;
	teamTypeUnsafeUgc: string | undefined;
};

/**
 * Represents an Avatar that is returned by JIRA. Note that fields that are not consumed are not
 * included in the type.
 */
export type ServerAvatar = {
	id: string;
	isSystemAvatar: boolean;
	isSelected: boolean;
	urls: {
		[key: string]: string;
	};
	fileName?: string;
};

/**
 * Response payload type for requests to
 * /rest/api/2/universal_avatar/type/project/owner/{projectIdOrKey}.
 */
export type ProjectAvatarsResponse = {
	system: ServerAvatar[];
	custom: ServerAvatar[];
	hasError?: boolean;
};

/**
 * Given the project and the avatar id determining the URL for a 128 x 128 image of this avatar.
 *
 * @param context - context for the current project
 * @param avatarId - id of the avatar to get the url for
 * @returns the full url path for this avatar
 */
export const getProjectAvatarUrl = (context: ProjectContext, avatarId: number): string =>
	`${context.baseUrl}/secure/viewavatar?size=xxxlarge@2x&avatarId=${avatarId}&avatarType=project`;

/**
 * Merge the two sets of avatars for the project into a single list.
 *
 * @param avatarsResponse - the response for the avatars for the project
 * @returns the list of avatars (system and custom) for the project
 */
const mergeSystemAndCustomAvatars = (avatarsResponse: ProjectAvatarsResponse): ServerAvatar[] =>
	avatarsResponse.system.concat(avatarsResponse.custom);

/**
 * Convert an avatar retrieved from the server to the type expected by the service.
 *
 * @param context - context for the current project
 * @param avatar - avatar details return from the server that need to be converted
 * @returns the type expected by the service
 */
const transformServerAvatarToServiceAvatar = (
	context: ProjectContext,
	avatar: ServerAvatar,
): AvatarDetails => {
	const avatarId: number = parseInt(avatar.id, 10);
	return {
		id: avatarId,
		avatarUrl: getProjectAvatarUrl(context, avatarId),
		fileName: avatar?.fileName,
	};
};

/**
 * Returns the end point url based on passed project context
 */
export const getProjectDetailsEndpointUrlOld = (context: ProjectContext): string =>
	`${context.baseUrl}/rest/api/2/project/${String(context.projectId)}`;

export const getProjectDetailsEndpointUrlWithExpandedProjectKeys = (
	context: ProjectContext,
): string => `/rest/api/2/project/${String(context.projectId)}?expand=projectKeys`;

export const getProjectDetailsEndpointUrl = (context: ProjectContext) =>
	fg('hix-4189-releasing-historical-project-keys')
		? getProjectDetailsEndpointUrlWithExpandedProjectKeys(context)
		: getProjectDetailsEndpointUrlOld(context);

/**
 * Returns the end point url based on passed project context
 */
export const getProjectTeamTypeEndpointUrl = (context: ProjectContext): string =>
	`/rest/servicedesk/latest/project/properties/${String(context.projectId)}/teamType`;

/**
 * Url for getting all of the avatars that can be assigned to the project
 *
 * @param context the context of the project to get avatars for
 * @returns the end point for getting the predefined avatars for the project
 */
export const getPredefinedAvatarsEndpointUrl = (context: ProjectContext): string =>
	`${context.baseUrl}/rest/api/2/universal_avatar/type/project/owner/${String(context.projectId)}`;
/**
 * Get the avatar url for the project's currently selected avatar.
 *
 * @param context - context for the current project
 * @param projectDetails - the project details for the project
 * @returns the avatar details that is currently selected for the project
 */
const getSelectedAvatar = (
	context: ProjectContext,
	projectDetails: ServerProjectDetailsResponse,
): AvatarDetails => {
	const avatarUrl: string = projectDetails.avatarUrls['48x48'];

	const avatarIdMatchResult = avatarUrl.match(/\/avatar\/(\d+)/);

	if (!avatarIdMatchResult || avatarIdMatchResult.length < 1) {
		logError(
			PACKAGE_NAME,
			toErrorId('getSelectedAvatar'),
			toErrorMessage('Could not find selected avatar id for project'),
		);

		// Fail safe to zero so that the page will load
		return {
			id: 0,
			avatarUrl: getProjectAvatarUrl(context, 0),
		};
	}

	const avatarId: number = parseInt(avatarIdMatchResult[1], 10);

	return {
		id: avatarId,
		avatarUrl: getProjectAvatarUrl(context, avatarId),
	};
};

const requestProjectDetails = (context: ProjectContext): Promise<ServerProjectDetailsResponse> =>
	famaCatchAndLogFetchError(
		performGetRequest(getProjectDetailsEndpointUrl(context)),
		PACKAGE_NAME,
		toErrorId('getProjectDetails'),
		toErrorMessage('Failed to fetch project details'),
	);

const requestProjectTeamType = (context: ProjectContext): Promise<ServerProjectTeamTypeResponse> =>
	famaCatchAndLogFetchError(
		performGetRequest(getProjectTeamTypeEndpointUrl(context)),
		PACKAGE_NAME,
		toErrorId('getProjectTeamType'),
		toErrorMessage('failed to fetch project team type'),
	).catch((error) => {
		if (error.statusCode === 404) {
			return {
				teamType: undefined,
				teamTypeUnsafeUgc: undefined,
			};
		}
		throw error;
	});

const requestPredefinedAvatars = (context: ProjectContext): Promise<ProjectAvatarsResponse> =>
	famaCatchAndLogFetchError(
		performGetRequest(getPredefinedAvatarsEndpointUrl(context)),
		PACKAGE_NAME,
		toErrorId('getPredefinedAvatars'),
		toErrorMessage('Failed to fetch avatars for project details'),
	).catch(() => ({
		hasError: true,
		system: [],
		custom: [],
	}));

const getProjectDetails = (context: ProjectContext): Promise<ProjectDetails> =>
	Promise.all([requestProjectDetails(context), requestPredefinedAvatars(context)]).then(
		async ([projectDetails, avatars]: [ServerProjectDetailsResponse, ProjectAvatarsResponse]) => {
			let teamType;
			let projectTeamTypeFreeText;
			// We want to load this details ONLY if user lands on JSM's page
			if (projectDetails.projectTypeKey && projectDetails.projectTypeKey === 'service_desk') {
				const projectTeamResult = await requestProjectTeamType(context);
				teamType = projectTeamResult.teamType;
				projectTeamTypeFreeText = projectTeamResult.teamTypeUnsafeUgc;
			}
			const selectedAvatar: AvatarDetails = getSelectedAvatar(context, projectDetails);
			const predefinedAvatars: AvatarDetails[] = mergeSystemAndCustomAvatars(avatars).map(
				(avatar) => transformServerAvatarToServiceAvatar(context, avatar),
			);

			// We want to make sure that the predefined avatar list will always show the currently
			// selected avatar if it is not the fail safe avatar (id = 0).
			const hasNoPredefinedAvatars: boolean = predefinedAvatars.length === 0;
			const isFailSafeSelectedAvatar = selectedAvatar.id === 0;
			if (hasNoPredefinedAvatars && !isFailSafeSelectedAvatar) {
				predefinedAvatars.push(selectedAvatar);
			}

			const historicalProjectKeys = fg('hix-4189-releasing-historical-project-keys')
				? projectDetails.projectKeys.filter((key) => key !== projectDetails.key)
				: [];

			return {
				id: parseInt(projectDetails.id, 10),
				key: projectDetails.key,
				name: projectDetails.name,
				url: projectDetails.url,
				avatar: selectedAvatar,
				predefinedAvatars,
				projectCategoryId: projectDetails.projectCategory
					? projectDetails.projectCategory.id
					: undefined,
				hasFetchAvatarError: avatars.hasError || false,
				projectLeadAccountId: projectDetails.lead ? projectDetails.lead.accountId : toAccountId(''),
				assigneeType: projectDetails.assigneeType,
				description: projectDetails.description,
				simplified: projectDetails.simplified,
				projectTeamType: teamType,
				projectTeamTypeFreeText,
				historicalProjectKeys,
			};
		},
	);

export default getProjectDetails;
